// src/context/ToastContext.jsx
// Leichtgewichtiges globales Toast-Benachrichtigungssystem
// Ersetzt native alert()-Aufrufe durch animierte In-App-Meldungen

import { createContext, useCallback, useContext, useRef, useState } from 'react';

const ToastContext = createContext(null);

let toastIdCounter = 0;

/**
 * ToastProvider – stellt showToast() global bereit.
 * Toasts verschwinden automatisch nach `duration` Millisekunden.
 */
export function ToastProvider({ children }) {
  const [toasts, setToasts] = useState([]);
  const timersRef = useRef({});

  const dismiss = useCallback((id) => {
    setToasts((prev) => prev.map((t) => (t.id === id ? { ...t, leaving: true } : t)));
    // Nach Animation aus dem DOM entfernen
    setTimeout(() => {
      setToasts((prev) => prev.filter((t) => t.id !== id));
      delete timersRef.current[id];
    }, 350);
  }, []);

  const showToast = useCallback(
    ({ message, type = 'info', duration = 4000 }) => {
      const id = ++toastIdCounter;
      setToasts((prev) => {
        // Maximal 4 gleichzeitige Toasts
        const next = prev.length >= 4 ? prev.slice(1) : prev;
        return [...next, { id, message, type, leaving: false }];
      });

      const timer = setTimeout(() => dismiss(id), duration);
      timersRef.current[id] = timer;

      return id;
    },
    [dismiss]
  );

  // Convenience-Methoden
  const toast = {
    success: (message, opts) => showToast({ message, type: 'success', ...opts }),
    error: (message, opts) => showToast({ message, type: 'error', ...opts }),
    info: (message, opts) => showToast({ message, type: 'info', ...opts }),
    warning: (message, opts) => showToast({ message, type: 'warning', ...opts }),
    dismiss,
  };

  return (
    <ToastContext.Provider value={toast}>
      {children}
      <ToastContainer toasts={toasts} onDismiss={dismiss} />
    </ToastContext.Provider>
  );
}

/** useToast – Hook zum Auslösen von Toasts aus beliebigen Komponenten. */
export function useToast() {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('useToast muss innerhalb eines <ToastProvider> verwendet werden.');
  return ctx;
}

// ── Toast Container (inline, kein extra File nötig) ──────────────────────────
const ICONS = {
  success: '✓',
  error: '✕',
  warning: '⚠',
  info: 'ℹ',
};

function ToastContainer({ toasts, onDismiss }) {
  if (!toasts.length) return null;

  return (
    <div className="toast-stack" role="region" aria-label="Benachrichtigungen" aria-live="polite">
      {toasts.map((t) => (
        <div
          key={t.id}
          className={`toast toast--${t.type}${t.leaving ? ' toast--leaving' : ''}`}
          role="alert"
        >
          <span className="toast__icon" aria-hidden="true">
            {ICONS[t.type]}
          </span>
          <span className="toast__message">{t.message}</span>
          <button
            className="toast__close"
            onClick={() => onDismiss(t.id)}
            aria-label="Benachrichtigung schließen"
          >
            ×
          </button>
        </div>
      ))}
    </div>
  );
}

export default ToastContext;
